import React, { createContext, useContext, useId, useLayoutEffect, useRef, useState } from "react";
import { Legend, ResponsiveContainer, Tooltip } from "recharts";

type ChartConfigItem = {
  label?: React.ReactNode;
  color?: string;
};

export type ChartConfig = Record<string, ChartConfigItem>;

const ChartContext = createContext<{ config: ChartConfig } | null>(null);

function useChart() {
  const context = useContext(ChartContext);
  if (!context) throw new Error("Chart components must be used inside ChartContainer");
  return context;
}

function chartStyle(config: ChartConfig): React.CSSProperties {
  return Object.fromEntries(
    Object.entries(config)
      .filter(([, item]) => item.color)
      .map(([key, item]) => [`--color-${key}`, item.color]),
  ) as React.CSSProperties;
}

export function ChartContainer({
  config,
  className = "",
  children,
}: {
  config: ChartConfig;
  className?: string;
  children: React.ReactElement;
}) {
  const chartId = useId().replace(/:/g, "");
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null);

  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const update = () => {
      const bounds = node.getBoundingClientRect();
      if (bounds.width <= 0 || bounds.height <= 0) return;
      setDimensions((current) => {
        if (current?.width === bounds.width && current.height === bounds.height) return current;
        return { width: bounds.width, height: bounds.height };
      });
    };
    update();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    }
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <ChartContext.Provider value={{ config }}>
      <div ref={containerRef} className={`chart-container ${className}`} data-chart={chartId} style={chartStyle(config)}>
        {dimensions ? <ResponsiveContainer width="100%" height="100%" initialDimension={dimensions}>{children}</ResponsiveContainer> : null}
      </div>
    </ChartContext.Provider>
  );
}

type ChartTooltipRuntimeProps = {
  active?: boolean;
  payload?: Array<{
    color?: string;
    dataKey?: string | number;
    name?: string | number;
    value?: string | number;
  }>;
  label?: string | number;
};

type ChartLegendRuntimeProps = {
  payload?: Array<{
    color?: string;
    dataKey?: string | number;
    value?: string | number;
  }>;
};

export function ChartTooltip(props: Record<string, unknown>) {
  return <Tooltip cursor={{ stroke: "rgba(15, 23, 42, 0.12)", strokeWidth: 1 }} {...props} />;
}

export function ChartTooltipContent({
  active,
  payload,
  label,
  labelFormatter,
  valueFormatter,
}: ChartTooltipRuntimeProps & {
  labelFormatter?: (label: string | number) => React.ReactNode;
  valueFormatter?: (value: number, name: string) => React.ReactNode;
}) {
  const { config } = useChart();
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <strong>{labelFormatter ? labelFormatter(label as string | number) : label}</strong>
      {payload.map((item) => {
        const key = String(item.dataKey || item.name || "");
        const itemConfig = config[key];
        const color = item.color || itemConfig?.color || `var(--color-${key})`;
        return (
          <span className="chart-tooltip-row" key={key}>
            <i style={{ backgroundColor: color }}></i>
            <span>{itemConfig?.label || item.name || key}</span>
            <b>{valueFormatter ? valueFormatter(Number(item.value || 0), key) : item.value}</b>
          </span>
        );
      })}
    </div>
  );
}

export function ChartLegend(props: Record<string, unknown>) {
  return <Legend verticalAlign="top" align="right" iconType="circle" wrapperStyle={{ paddingBottom: 16 }} {...props} />;
}

export function ChartLegendContent({ payload }: ChartLegendRuntimeProps) {
  const { config } = useChart();
  if (!payload?.length) return null;
  return (
    <div className="chart-legend">
      {payload.map((item) => {
        const key = String(item.dataKey || item.value || "");
        const itemConfig = config[key];
        const color = item.color || itemConfig?.color || `var(--color-${key})`;
        return (
          <span key={key}>
            <i style={{ backgroundColor: color }}></i>
            {itemConfig?.label || item.value || key}
          </span>
        );
      })}
    </div>
  );
}
